import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../../utils/pricing';

// 紺帯番号バンド(home/page.tsx NavyBand と同一文法・移植)。帯=区切り(#31)／帯直後に同一文言見出しを反復しない(#33)。
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// ===== ハウススチュワード詳細 全面最新化（Implementer A スコープ） =====
// 土台: about/overview + home。カタログ: Hero=BB-02 / H2-02系 / SEC-N02,N04,N07,N08翻案。
// 事実正典（創作禁止）: 現行 working tree services/house-steward（特徴・3人体制・シーン・料金）+ hub料金表
//   + mng-thebutler/00_事業説明.md L78(ハウススチュワード=24時間3交代・常駐)。料金データ変更禁止。
// 注記: 旧実文の破損箇所（「完全20日間」「来客の20日間」「20日間可能な環境」等、24時間サービスに
//   矛盾する明白な文字化け）は、24時間テーマの文脈に沿って整合的に修正（新事実の創作はしない）。

export default function HouseStewardPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '執事サービス', href: '/butler-services/' },
    { label: 'ハウススチュワード' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { en: '24 Hours', ja: '24時間365日対応', d: '深夜の急な要望、早朝の準備、休日の特別なリクエストなど、時間を問わず常に執事が待機しています。お客様の生活に「対応時間外」という言葉は存在しません。' },
    { en: 'Team of Three', ja: '3人体制による専門性', d: '各時間帯に最適な執事が配置され、朝食からビジネスサポート、ディナーまで、すべての場面で最高品質を提供します。急な休暇や体調不良時もチームでカバーし、サービスが途切れません。' },
    { en: 'Personalized', ja: '完全パーソナライズド', d: '3人の執事がお客様の生活のすべてを把握し、好み・習慣・価値観を深く理解します。言葉にする前に要望が叶う、真の執事サービスを実現します。' },
    { en: 'Security', ja: '最高水準のセキュリティ', d: '24時間常に執事が在宅することで、防犯面でも大きな安心があります。不審者の対応、緊急時の連絡、重要な来客の確認など、セキュリティ機能も果たします。' },
    { en: 'Whole Family', ja: '家族全体のサポート', d: 'ご家族全員の生活を包括的に支援します。ご主人様のビジネス、奥様の社交活動、お子様の教育サポート、ご高齢のご家族のケアなど、すべてを調整しながら最適化します。' },
  ];

  const shifts = [
    { icon: '朝', t: '朝番執事', time: '8:00 – 16:00', d: '早朝から午後にかけての時間帯を担当。起床サポート、朝食準備、午前中の家事、昼食準備、午後の予定準備などを行い、一日の始まりを完璧に整えます。' },
    { icon: '昼', t: '日中番執事', time: '13:00 – 21:00', d: '昼から夜にかけての最も活動的な時間帯を担当。来客対応、ビジネスサポート、外出準備、買い物、夕食準備、ディナーサービスなどを行います。朝番から引き継ぎ、シームレスに継続します。' },
    { icon: '夜', t: '夜番執事', time: '20:00 – 翌8:00', d: '夜から早朝までの時間帯を担当。夕食後の片付け、就寝準備、夜間の見回り、深夜の要望対応、早朝の準備などを行い、安心して休息できる環境を整えます。' },
  ];

  const scenes = [
    { t: '超多忙な経営者・著名人', d: 'ビジネスや創作活動に完全に集中するため、生活のすべてを執事チームに任せたいトップエグゼクティブの方。早朝から深夜まであらゆる雑務から解放され、本当に重要なことだけに時間を使えます。' },
    { t: 'ファミリーオフィスの運営', d: '複数の事業や投資を管理する富裕層ファミリーのため、住居とオフィスの両方を統合管理。家族全員のスケジュール調整、来客対応、重要書類の管理、秘書機能などを提供します。' },
    { t: '国際的なVIPの日本拠点', d: '頻繁に海外と日本を行き来するグローバルVIPの拠点として。時差のある海外との連絡対応、突然の予定変更への即応など、24時間対応可能な環境を整えます。' },
    { t: '大規模邸宅・複数物件の管理', d: '広大な邸宅や複数の不動産を所有される方のため、24時間体制での物件管理、スタッフ統括、セキュリティ管理、維持管理業者の手配を行います。' },
  ];

  const serviceGroups = [
    { t: '生活全般の完全管理', d: '家事全般、食事準備（朝・昼・夜・夜食）、買い物、洗濯、清掃、整理整頓、ペットケア、植物管理、季節対応' },
    { t: 'パーソナルアシスタント機能', d: 'スケジュール管理、予約代行、旅行手配、贈答品手配、衣類管理、重要書類整理、郵便物管理' },
    { t: 'ビジネス・秘書サポート', d: '来客対応（24時間）、会議準備、電話対応、メール確認補助、資料整理、オフィス環境管理' },
    { t: '健康・ウェルネスサポート', d: '服薬管理、通院付き添い、健康状態観察、栄養管理、運動サポート、医療機関との連携' },
    { t: 'セキュリティ・邸宅管理', d: '24時間見守り、不審者対応、設備点検、修繕手配、業者立ち会い、緊急時対応' },
    { t: 'ファミリー・社交サポート', d: '家族全員のスケジュール調整、子どもの教育サポート、高齢家族のケア、ホームパーティー企画・実施、社交カレンダー管理' },
  ];

  const faqs = [
    ['なぜ3人体制なのですか？', '24時間365日、常に最高品質のサービスを提供するためです。1人の執事では不可能な継続性と専門性を、チームで両立します。'],
    ['執事が休みの日はどうなりますか？', '3人がローテーションで休暇を取るため、お客様から見れば常に執事が対応可能です。サービスが途切れることはありません。'],
    ['プライバシーは守られますか？', '全執事が厳格な秘密保持契約を締結し、協会の倫理規定に従います。定期的な守秘義務研修も実施しています。'],
    ['長期契約が必要ですか？', '3ヶ月以上を推奨していますが、1ヶ月単位でのご契約も可能です。チームが完全にお客様を理解するには3ヶ月程度が目安です。'],
    ['他のスタッフ（運転手、庭師など）との連携は？', '執事チームが全スタッフを統括し、シームレスな連携を実現します。急な変更や特別な要望にも即座に対応します。'],
  ];

  const related = [
    { name: 'Footman', ja: 'フットマン', cad: '3時間からのスポット利用', price: yenInc(jbaDispatchExTax.footmanBase), unit: '円〜（税込）', href: '/services/footman/' },
    { name: 'Butler', ja: 'バトラー', cad: '1日8時間の専属サービス', price: yenInc(jbaDispatchExTax.butlerBase), unit: '円〜（税込）', href: '/services/butler/' },
    { name: 'Second Steward', ja: 'セカンドスチュワード', cad: '1ヶ月20日間の継続利用', price: yenInc(jbaDispatchExTax.secondStewardMonthly), unit: '円〜（税込）', href: '/services/second-steward/' },
  ];

  return (
    <Layout>
      <title>ハウススチュワード｜24時間・3人交代の常駐執事サービスと料金 | 全日本執事協会 THE BUTLER</title>
      <meta name="description" content={`THE BUTLER のハウススチュワードは、3人の熟練執事が8時間交代で常駐する24時間365日の最高峰プラン。邸宅全体を統括し、VVIP・超富裕層・ファミリーオフィスの生活を支えます。料金${yenInc(jbaDispatchExTax.houseStewardMonthly)}円（税込・月額）。`} />
      <link rel="canonical" href="https://butler-association.jp/services/house-steward" />

      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-p-043-svc-housesteward.webp" alt="ハウススチュワードサービス。熟練執事が邸宅を統括する最高峰の体制" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.94)_0%,rgba(16,20,26,0.74)_48%,rgba(16,20,26,0.38)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Plan Ⅳ — House Steward</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.1vw,40px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">24時間、3人の執事が</span>
              <span className="block">邸宅を統括する</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              ハウススチュワードは、全日本執事協会が誇る最高峰のプランです。1日24時間、3人の熟練執事が8時間交代で常時待機し、あらゆるニーズに即座に対応します。VVIP・超富裕層・ファミリーオフィスのお客様に、邸宅全体を統括する究極のパートナーシップを、途切れることなくお届けします。
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <NavyBand no="I" ja="このサービスについて" en="Overview" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S1 特徴 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              24時間が実現する、<span className="text-[#a38e65]">究極のライフスタイル</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>ハウススチュワードは、執事サービスの概念を超えた、完全なライフスタイルマネジメントです。朝・昼・夜、それぞれの時間帯に最適化された専門執事が8時間交代で常駐することで、お客様は24時間365日、いつでも最高品質のサービスを受けられます。これは単なる人材提供ではなく、人生そのものを支える究極のパートナーシップです。</p>
              <p>3人の執事がチームを組むことで、個人では不可能な継続性と専門性を両立します。それぞれが最も得意とする時間帯を担当し、定期的なチームミーティングで情報を完全に共有。まるで一人の執事が24時間付き添っているかのような、シームレスなサービスを提供します。</p>
              <p>全日本執事協会が130年以上の歴史で培った執事教育の集大成として、Master Butler資格保持者を中心とした最上級の人材のみが携わります。彼らは技術に優れているだけでなく、品格、教養、洞察力、そして何よりも「仕える心」を体現した、真のプロフェッショナルです。</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 mt-10">
              {features.map((f) => (
                <div key={f.ja} className="border-t border-[#e2ddd2] pt-6">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5">{f.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="II" ja="三交代制" en="The Three Shifts" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S2 3人交代体制（紺パネル・タイムライン）= 新バリエーション
               由来注記: overview S3 紺パネル + S5 タイムラインを合成し、3交代のシフト帯を紺地に時刻軸で示す。トップに交代制シフト型は無いため新造。 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              3人の執事による完璧な交代制
            </h2>
            <div className="grid lg:grid-cols-[340px_1fr] gap-8 lg:gap-12 items-start">
              <img src="/images/generated/jba-fs-238-services_house-steward-v6.webp" alt="執事チームによる24時間体制のサービス" className="w-full h-64 lg:h-[380px] object-cover border border-[#a38e65]/30" />
              <div className="bg-[#1a2942] p-7 sm:p-9">
                <div className="space-y-6">
                  {shifts.map((s) => (
                    <div key={s.t} className="flex gap-5 border-b border-white/10 pb-6 last:border-0 last:pb-0">
                      <span className="shrink-0 w-11 h-11 border border-[#a38e65]/60 flex items-center justify-center jba-display text-[#d7b779] text-lg">{s.icon}</span>
                      <div>
                        <div className="flex flex-wrap items-baseline gap-x-4 mb-1.5">
                          <h3 className="jba-display font-medium text-[#f8f6f0] text-lg">{s.t}</h3>
                          <span className="font-['Playfair_Display',serif] text-[#d7b779] text-sm tracking-[0.08em]">{s.time}</span>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">{s.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="w-11 h-px bg-[#a38e65] mt-7 mb-5"></div>
                <ul className="space-y-2 font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.8]">
                  <li>各執事が1時間の引き継ぎ時間を設け、完全な情報共有を行います</li>
                  <li>週に1回のチームミーティングで、サービス品質の向上を図ります</li>
                  <li>緊急時は3人全員が即座に対応できる体制を整えています</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="III" ja="ご利用シーン" en="Scenes" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S3 シーン（画像split） */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              最高水準の生活を求める方へ
            </h2>
            <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {scenes.map((s) => (
                  <div key={s.t} className="border-l-2 border-[#a38e65]/40 pl-4">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                  </div>
                ))}
              </div>
              <img src="/images/generated/jba-fs-239-services_house-steward-v6.webp" alt="ハウススチュワードによる邸宅管理" className="w-full h-64 lg:h-[360px] object-cover border border-[#a38e65]/30 order-first lg:order-none" />
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="IV" ja="提供サービス" en="What We Do" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S4 提供サービス内容（画像split） */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              生活のあらゆる側面を完全サポート
            </h2>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                {serviceGroups.map((g) => (
                  <div key={g.t} className="border-t border-[#e2ddd2] pt-6">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">{g.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{g.d}</p>
                  </div>
                ))}
              </div>
              <img src="/images/generated/jba-fs-240-services_house-steward-v6.webp" alt="ハウススチュワードによる完璧なサービス提供" className="w-full h-64 lg:h-[420px] object-cover border border-[#a38e65]/30 order-first lg:order-none" />
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="V" ja="料金" en="Pricing" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S5 料金 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              料金
            </h2>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start">
              <div className="bg-[#1a2942] p-8 text-center">
                <div className="font-['Noto_Serif_JP',serif] text-[34px] text-[#f8f6f0] leading-none">{yenInc(jbaDispatchExTax.houseStewardMonthly)}<span className="text-base text-[#d7b779] ml-1">円</span></div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] mt-3">税込・月額</p>
                <p className="font-['Noto_Serif_JP',serif] text-xs text-[#ded8cc] mt-4 leading-[1.7]">1日24時間×30日間<br />3人の執事による交代制</p>
              </div>
              <div>
                <dl className="border-t-2 border-[#1a2942]">
                  {[
                    ['月額料金', `${yenInc(jbaDispatchExTax.houseStewardMonthly)}円（税込）`],
                    ['契約期間', '3ヶ月以上を推奨（1ヶ月単位での契約も可能）'],
                    ['執事指名', '無料（3人全員の指名可能）'],
                    ['交通費', '月額定額制に含む'],
                    ['初期費用', 'セットアップ費用 500,000円（初回のみ・邸宅調査／システム構築／チーム研修）'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] shrink-0">{k}</dt>
                      <dd className="font-['Noto_Serif_JP',serif] text-[16px] text-[#1a2942] text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mt-6">
                  <span className="text-[#8f7a56]">お支払い：</span>銀行振込（月払い・四半期払い・年払い）。6ヶ月一括払いで3%割引、年間一括払いで5%割引。
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VI" ja="よくあるご質問" en="FAQ" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S6 FAQ */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくあるご質問
            </h2>
            <div className="max-w-3xl divide-y divide-[#e2ddd2] border-t border-[#e2ddd2]">
              {faqs.map(([q, a]) => (
                <div key={q} className="py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">Q. {q}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VII" ja="その他のプラン" en="Other Plans" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S7 関連サービス */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              他のプランと比べる
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              まずお試しならフットマン、特別な一日にはバトラー、月20日の継続ならセカンドスチュワードへ。
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <a key={r.name} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] transition-colors duration-300 p-6">
                  <span className="block font-['Playfair_Display',serif] text-[13px] tracking-[0.1em] text-[#8f7a56] mb-1">{r.name}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 group-hover:text-[#a38e65] transition-colors">{r.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mb-4">{r.cad}</p>
                  <div className="font-['Noto_Serif_JP',serif] text-[22px] text-[#1a2942]">{r.price}<span className="text-sm text-[#8f7a56] ml-1">{r.unit}</span></div>
                </a>
              ))}
            </div>
            <div className="mt-8"><a href="/butler-services/" className={BTN08_ON_LIGHT}>執事サービス一覧へ戻る</a></div>
          </section>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S8 CTA */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <h2 className="jba-display font-medium text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.5] mb-4">究極の執事サービスをご体験ください</h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                秘密厳守でご相談を承ります。お問い合わせの際は「ハウススチュワード希望」とお伝えください。初回面談では代表理事が直接お伺いし、詳細なヒアリングを行います。
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせフォームへ</a>
                <span className="font-['Noto_Serif_JP',serif] text-[#e7e2d8] text-[15px]">お電話：03-6824-4086（平日10:00-18:00）</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
